import { LibraryIcon } from '@components/Icons/LibraryIcon';
import { LibraryMediaActionBar } from '@components/MediaActionBar/LibraryMediaActionBar';
import type { MediaActionBarLayoutProps } from '@components/MediaActionBar/MediaActionBar';
import { MediaVerbsActionBar } from '@components/MediaActionBar/MediaVerbsActionBar';
import { MobileModal } from '@components/MobileModal/MobileModal';
import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { Toast } from '@components/Toast/Toast';
import { VideoSkeleton } from '@components/VideoSkeleton/VideoSkeleton';
import { useMediaData } from '@lib/data/mediaData';
import { NarrativeThreadsFile, ScenesFile } from '@lib/data/story';
import { useTranscript } from '@lib/data/transcript';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { isTextEntryTarget, KEY_SHORTCUTS, matchesKey } from '@lib/keyboard/shortcuts';
import { FOLDER_ROOT_PATH, folderTrail } from '@lib/library/folderPath';
import { anchorStart, formatAnchorLabel } from '@lib/media/commentAnchor';
import { type CommentSort, DEFAULT_COMMENT_SORT } from '@lib/media/commentSort';
import type { MediaActionControl, MediaMenuHostItem } from '@lib/media/mediaActions';
import { ModalTab } from '@lib/media/modalTab';
import { askAgentAboutMoments } from '@lib/starzero-agents';
import { downloadSrt } from '@lib/transcript/srt';
import { showToast } from '@lib/utils/toastmaster';
import { formatClockLabel, formatCredits, formatCreditsWithUnit } from '@lib/utils/units';
import { copyAppLink, getCommentUrl, getLibraryUrl } from '@lib/utils/url';
import CloseIcon from '@mui/icons-material/Close';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DownloadIcon from '@mui/icons-material/Download';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { IconButton, Modal, Skeleton, Tooltip } from '@mui/material';
import { FolderGlyph } from '@pages/Library/components/Folders/FolderGlyph';
import { isProcessingWithoutVideo } from '@pages/Library/utils/mediaStatus';
import { billingStore } from '@stores/Billing';
import { debugStore } from '@stores/Debug';
import { SearchResultItem } from '@stores/Library/LibrarySearch';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { type MediaComment, MediaCommentsStore } from '@stores/MediaComments';
import { useModalRegistration } from '@stores/ModalStore';
import { notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import { VideoPlayerStore } from '@stores/VideoPlayer';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { useRemoveWatermark } from 'hooks/useRemoveWatermark';
import { observer } from 'mobx-react';
import {
  forwardRef,
  MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CommentsPanel } from './components/CommentsPanel/CommentsPanel';
import { MobileDownloadDrawer } from './components/MobileDownloadDrawer/MobileDownloadDrawer';
import { PanelTabs } from './components/PanelTabs/PanelTabs';
import { ProcessingPlaceholder } from './components/ProcessingPlaceholder/ProcessingPlaceholder';
import { ScenesPanel } from './components/ScenesPanel/ScenesPanel';
import { SelectionBar, SelectionHint, SelectionSlot } from './components/SelectionBar/SelectionBar';
import { TranscriptLoader } from './components/TranscriptLoader/TranscriptLoader';
import {
  CLIP_REBUILD_DELAY_MS,
  VideoPlayer,
  VideoPlayerRef,
} from './components/VideoPlayer/VideoPlayer';
import { VideoProcessingPlaceholder } from './components/VideoProcessingPlaceholder/VideoProcessingPlaceholder';
import { VideoTimeline } from './components/VideoTimeline/VideoTimeline';
import {
  VisualDescriptionLoader,
  VisualDescriptions,
} from './components/VisualDescriptionLoader/VisualDescriptionLoader';
import css from './MomentModal.module.scss';

/**
 * How far the marked range must move from the bounds it was seeded with before the modal calls
 * it edited. A hair, in seconds — the range comes back off a pointer position, so an exact
 * float comparison would call putting a handle back where it was an edit.
 */
const SELECTION_EDIT_EPSILON_SECONDS = 0.05;

export type MomentModalRef = {
  open: () => void;
  close: () => void;
};

// Extend MomentModalProps to accept an optional onClose callback and open prop
export type MomentModalProps = (
  | {
      variant: 'search-result';
      result: SearchResultItem;
      media: Media;
      /**
       * Which page opened it. Two very different callers share this variant — the search
       * grid and Story Atlas — and the variant alone can't tell them apart, so anything
       * that depends on the page has to ask for it explicitly rather than sniff the URL.
       *
       * It gates the CLIP-scoped agent verb, which is search-only because the handoff reads
       * `multiLibSearch.resultType` for its segment type and that is meaningless outside a
       * search. Atlas gets the media-scoped verb instead, and reports its own `AgentsSurface`
       * member — added with the action bar, so those exits no longer land in the library's
       * numbers.
       */
      surface: 'search' | 'atlas';
    }
  | {
      variant: 'library-media-card';
      media: Media;
      /**
       * Where to put the playhead on open, in seconds — the `?t=` on a shared link.
       *
       * Library variant only, because it's the only one whose URL carries the media: a
       * search result's modal is opened from a grid, has no address of its own, and already
       * positions the playhead from `result.startTime`.
       *
       * `undefined` means "wherever it would normally open", NOT zero. A link with no `t`,
       * and one whose `t` we couldn't parse, must both leave the playhead alone.
       */
      startAt?: number;
      /**
       * The comment a comment link (`?comment=`) points to. Selected, and its span marked, once
       * this media's comments have been read back. Library variant only, for the same reason
       * as `startAt`. The link also carries `?t=` and `?tab=comments`, which do the seeking and
       * the panel through their own paths — this only picks the comment out.
       */
      commentId?: string;
    }
) & {
  onClose?: () => void;
  open?: boolean;
  tab?: ModalTab;
  onTabChange?: (tab: ModalTab) => void;
};

export const MomentModal = observer(
  forwardRef<MomentModalRef, MomentModalProps>((props, ref) => {
    const { media, open: externalOpen, tab, onTabChange } = props;
    const navigateTo = useNavigate();
    const isSearchResult = props.variant === 'search-result';
    const result = isSearchResult ? props.result : null;
    const startAt = props.variant === 'library-media-card' ? props.startAt : undefined;
    const linkedCommentId = props.variant === 'library-media-card' ? props.commentId : undefined;
    const surface = isSearchResult ? props.surface : null;

    const isMobile = useIsMobile();
    const libraryManager = useLibraryManager();
    const uiPreferences = useUIPreferences();
    const videoPlayerRef = useRef<VideoPlayerRef>(null);
    const playerStore = useRef<VideoPlayerStore>(new VideoPlayerStore()).current;
    const [internalOpen, setInternalOpen] = useState(false);
    const [timelineZoomLevel, setTimelineZoomLevel] = useState<number | undefined>(undefined);
    const [isDownloadDrawerOpen, setIsDownloadDrawerOpen] = useState(false);

    // Use external open prop if provided, otherwise use internal state
    const open = externalOpen !== undefined ? externalOpen : internalOpen;
    useModalRegistration('moment', open);
    const [format, setFormat] = useState<'mp4' | 'gif' | 'both'>('mp4');

    // Look up the library once; the menu needs it only for the library variant, but the
    // transcript-only flag applies to both variants (search results too).
    const libraryForMedia = libraryManager.libraryById(media.libraryId);
    const library = !isSearchResult ? libraryForMedia : null;
    const isModifiable = library ? !library.isReadOnly : false;

    // Transcript-only libraries skip visual indexing, so there's no visual summary to show —
    // default to the transcript tab and mark the visual one unavailable.
    const isTranscriptOnly = !!libraryForMedia?.isTranscriptOnly;

    // For search results, use local state initialized from user preference
    const [rightPanel, setRightPanel] = useState<ModalTab>(() => {
      // If it's a search result with text, prefer transcript, otherwise use saved preference
      if (isSearchResult && result?.text) {
        return 'transcript';
      }
      return uiPreferences.momentModalDefaultTab;
    });
    const [isProcessingHovered, setIsProcessingHovered] = useState(false);

    const handlePanelChange = (panel: ModalTab) => {
      trackingStore.momentModalPanelChanged({
        panel,
        mediaId: media._id,
        libraryId: media.libraryId,
      });
      if (isSearchResult) {
        setRightPanel(panel);
        uiPreferences.setMomentModalDefaultTab(panel);
      } else {
        onTabChange?.(panel);
      }
    };

    // Use tab prop for library variant, local state for search variant. `currentPanel` below
    // narrows this by what the media actually turns out to have.
    const requestedPanel: ModalTab = isSearchResult ? rightPanel : tab || 'visual';
    const [isLoading, setIsLoading] = useState(true);

    // Check if media is still processing (transcript/visual not ready yet)
    // Debug flag allows simulating processing state for any media
    const isProcessing =
      ['queued', 'uploading', 'processing'].includes(media.status) ||
      debugStore.simulateProcessingModal;

    // Calculate total seconds for watermark removal cost
    const totalSeconds =
      isSearchResult && result
        ? (playerStore.moment?.end ?? result.endTime) -
          (playerStore.moment?.start ?? result.startTime)
        : 0;

    const {
      removeWatermark,
      setRemoveWatermark,
      watermarkRemovalCost,
      isWatermarkEnabled,
      watermarkRemovalTooltipText,
    } = useRemoveWatermark({
      totalSeconds,
    });

    const { data: transcript, error: transcriptError } = useTranscript(
      open && !isProcessing ? media.libraryId : undefined,
      open && !isProcessing ? media._id : undefined,
      media.status, // Cache key to trigger refetch when status changes
    );
    const { data: visualDescriptions, error: visualDescriptionsError } =
      useMediaData<VisualDescriptions>(
        'shot_descriptions.json',
        // Skip the visual fetch for transcript-only libraries — there's no visual data.
        open && !isProcessing && !isTranscriptOnly ? media.libraryId : undefined,
        open && !isProcessing && !isTranscriptOnly ? media._id : undefined,
        media.status, // Cache key to trigger refetch when status changes
      );

    // Fetched on open rather than when the Scenes tab is picked: whether the media HAS scenes
    // decides whether the toggle offers the tab at all, so the answer has to be in before the
    // user reaches for it. Both files are a few KB beside a transcript and an HLS stream.
    const { data: scenesFile, error: scenesError } = useMediaData<ScenesFile>(
      'scenes.json',
      open && !isProcessing ? media.libraryId : undefined,
      open && !isProcessing ? media._id : undefined,
      media.status, // Cache key to trigger refetch when status changes
    );
    // No error branch: the threads are the scene list's legend, not its content, so a failed
    // read hides the rail. `useMediaData` has already logged it.
    const { data: threadsFile } = useMediaData<NarrativeThreadsFile>(
      'narrative_threads.json',
      open && !isProcessing ? media.libraryId : undefined,
      open && !isProcessing ? media._id : undefined,
      media.status, // Cache key to trigger refetch when status changes
    );

    /**
     * The panel actually shown.
     *
     * Only one narrowing, and it is about a whole library rather than one media: a
     * transcript-only library has no visual descriptions, so a request for the Visual panel
     * lands on the transcript instead. Scenes deliberately gets NO equivalent — media without
     * a `scenes.json` still opens the panel, which says so. A tab the user pressed has to
     * open; silently landing them somewhere else is the same defect as a tab that ignores the
     * press.
     */
    const currentPanel: ModalTab =
      requestedPanel === 'visual' && isTranscriptOnly ? 'transcript' : requestedPanel;

    /**
     * This media's comments, for the panel, the tab's count and both sets of markers.
     *
     * Owned here rather than inside the panel because three of those four readers are outside
     * it: the count has to be right on a tab nobody has pressed, and the markers have to be on
     * the bar while the reader is on the Transcript.
     *
     * Keyed on the media, so the modal staying mounted across a change of media on the library
     * page cannot leave the previous one's markers on this one's timeline.
     */
    const commentsStore = useMemo(
      () => new MediaCommentsStore(media.libraryId, media._id),
      [media.libraryId, media._id],
    );

    /**
     * The Comments list's order. Here because the panel unmounts on every tab change. Kept for
     * as long as this modal is mounted, which spans the media opened in it — a reader who wants
     * newest first wants it on the next media too — but not stored on the account: it is a way
     * of reading one list, not a setting anyone should have to find to undo.
     */
    const [commentSort, setCommentSort] = useState<CommentSort>(DEFAULT_COMMENT_SORT);

    /**
     * The comment last gone to — from its timestamp in the list, or its marker on the scrub bar
     * or the filmstrip. It stays marked, in the list and as the white marker on the bar, until
     * another is gone to: it is the "you are here" of the comments, and a highlight that lifted
     * on a timer left the reader with nothing saying which note the player was showing.
     */
    const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);

    // A different media is a different set of comments; the last one's selection means nothing
    // here. Keyed on the store, which is keyed on the media.
    useEffect(() => setSelectedCommentId(null), [commentsStore]);

    /**
     * The selection bar's Comment button asked for the composer, and the composer has not taken
     * focus yet. A flag the composer clears, not an event, because the panel it lives in may not
     * be mounted at the moment of the press — another tab was showing.
     */
    const [composerFocusRequested, setComposerFocusRequested] = useState(false);
    const handleComposerFocused = useCallback(() => setComposerFocusRequested(false), []);

    // A library we have not read back yet is treated as writable: the API is the authority on
    // this, and hiding the composer on a perfectly ordinary library because its store has not
    // landed is the worse of the two wrong answers. A refused write says so.
    const canComment = !libraryForMedia?.isReadOnly;

    // Read on open rather than on mount: the modal is mounted long before it is shown, and a
    // media that is still processing shows a placeholder where this panel would be.
    //
    // `update()` rather than `get()`, so reopening a media picks up what was written since —
    // including from another tab. The rejection is discarded because it has already been
    // handled: it is on `commentsStore.error`, which is what `CommentsPanel` renders.
    useEffect(() => {
      if (!open || isProcessing) return;
      void commentsStore.update().catch(() => {});
      return () => commentsStore.abort();
    }, [open, isProcessing, commentsStore]);

    useEffect(() => {
      if (!open) {
        setSelectedCommentId(null);
        setComposerFocusRequested(false);
      }
    }, [open]);

    /**
     * Which `?t=` we've already honoured, as `<mediaId>:<seconds>`.
     *
     * The seek has to be one-shot. `isLoading` flips false from the player's `onReady`, which
     * comes off the video element's `canplay` — and `canplay` fires again after every seek, so
     * an unguarded effect would drag the user back to the shared timestamp each time they
     * scrubbed away from it.
     */
    const appliedStartAtRef = useRef<string | null>(null);

    // Handle modal open/close state changes
    useEffect(() => {
      if (open) {
        // Modal is opening, set up the video player store
        if (isSearchResult && result) {
          playerStore.setMoment({ start: result.startTime, end: result.endTime });
        } else {
          // For library media, show full video
          playerStore.setMoment(null);
          playerStore.setMode('full');
        }
      } else {
        // Modal is closing, reset the video player store
        playerStore.setMoment(null);
        playerStore.setMode('moment');
        playerStore.setCurrentTime(0);
        setIsLoading(true);
        // Re-arm the `?t=` seek below, so reopening the same link honours it again instead of
        // being deduped against the previous visit.
        appliedStartAtRef.current = null;
        appliedCommentLinkRef.current = null;
      }
    }, [open, isSearchResult, result, playerStore]);

    // Put the playhead where a shared link asked for it (`?t=`, library variant only).
    //
    // Gated on `!isLoading` rather than run on open: before the player is ready there is no
    // seekable range and the clip store is still being built from the media's duration, so an
    // earlier seek is silently dropped. Keying on `startAt` (not just on readiness) also covers
    // the address bar changing while the modal stays mounted.
    useEffect(() => {
      if (!open || isLoading || startAt === undefined) return;

      // The player's clip is built as `[0, metadata.video.duration ?? 0]`, so with the duration
      // missing every target clamps to 0 — the seek would silently land on the first frame.
      // Wait for it instead of burning the one-shot: the effect re-runs when it arrives, and
      // the ref is only stamped once the seek can actually be honoured. If it never arrives the
      // playhead stays where it already was, which beats a confident jump to 0:00.
      const duration = media.metadata?.video?.duration;
      if (!duration || duration <= 0) return;

      const key = `${media._id}:${startAt}`;
      if (appliedStartAtRef.current === key) return;
      appliedStartAtRef.current = key;

      // A `t` past the end lands just short of it rather than on the last frame, which would
      // show the ended state.
      const target = Math.min(startAt, Math.max(duration - 0.1, 0));

      videoPlayerRef.current?.scrubTo(target);
    }, [open, isLoading, startAt, media._id, media.metadata?.video?.duration]);

    /**
     * The playhead, for the kebab's "Copy link at 1:30" row — the other end of `startAt`.
     *
     * A getter rather than a value because only the kebab knows when it wants a reading: it
     * samples this once on open and holds that number, so the row's label and the link it
     * copies are the same timestamp. Handing the live value down instead would rebuild the
     * menu several times a second and relabel the row under the user's cursor.
     *
     * `undefined` while the player is still loading, because `currentTime` reads 0 then — and
     * a link stamped at the first frame is just a plain link with extra characters. The hook
     * treats 0 and `undefined` the same way, so this is belt-and-braces, but it keeps the
     * intent at the surface that knows the player's state.
     */
    const getCurrentPlayheadTime = useCallback(
      () => (isLoading ? undefined : playerStore.currentTime),
      [isLoading, playerStore],
    );

    const modeSwitchSeekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
      () => () => {
        if (modeSwitchSeekTimer.current) {
          clearTimeout(modeSwitchSeekTimer.current);
          modeSwitchSeekTimer.current = null;
        }
      },
      [],
    );

    /**
     * Scrub to an absolute media time once the mode change's clip rebuild has landed. Both
     * seeks in this file that cross a mode change go through here, so the modal has one timer
     * to cancel on unmount and one place that knows about the rebuild.
     */
    const scrubAfterClipRebuild = (seconds: number) => {
      if (modeSwitchSeekTimer.current) {
        clearTimeout(modeSwitchSeekTimer.current);
      }
      modeSwitchSeekTimer.current = setTimeout(() => {
        modeSwitchSeekTimer.current = null;
        videoPlayerRef.current?.scrubTo(seconds);
      }, CLIP_REBUILD_DELAY_MS);
    };

    /**
     * Put the playhead at an absolute time in the media, from a panel that addresses the WHOLE
     * media — the scene list — rather than the trimmed moment.
     *
     * In `moment` mode the player's clip is `[moment.start, moment.end]` and `scrubTo` clamps
     * to it, so a scene outside the moment would silently land on the moment's edge instead.
     * Widening to `full` first is the honest reading of the click, and the Moment / Full media
     * toggle sitting on the player flips with it, so the screen still says what the player is
     * doing. Inside the moment nothing changes — the moment stays framed.
     *
     * The scrub is deferred because the clip is rebuilt in an effect on the mode change;
     * scrubbing in the same tick clamps against the clip that is about to be replaced. Same
     * 10ms hop `VideoPlayer.handleModeChange` uses for the mirror-image case.
     */
    const seekToMediaTime = (seconds: number) => {
      const moment = playerStore.moment;
      const outsideMoment =
        playerStore.mode === 'moment' &&
        !!moment &&
        (seconds < moment.start || seconds > moment.end);

      if (!outsideMoment) {
        videoPlayerRef.current?.scrubTo(seconds);
        return;
      }

      // Deliberately no `videoModeChanged`: that event is the toggle's, and it carries no
      // `control` property to separate a press from this, so firing it here would report a
      // press nobody made.
      playerStore.setMode('full');
      scrubAfterClipRebuild(seconds);
    };

    // Close modal if media is in an invalid state (errored, cancelled, or empty)
    useEffect(() => {
      if (open && ['errored', 'cancelled', 'empty'].includes(media.status)) {
        if (media.status === 'errored') {
          // Rolling 60s merge — multiple errors for the same library fold
          // into one persistent card in the upload activity panel. No
          // fleeting toast; the panel card is the only surface.
          notificationStore.pushRichEventOrMerge({
            type: 'rich-event',
            variant: 'error',
            iconKind: 'processing-error',
            header: 'Processing failed',
            description: 'There was an error processing your media.',
            rows: [
              {
                key: media._id,
                filename: media.metadata?.name ?? media.metadata?.filename ?? 'Untitled',
                reason: 'Internal processing error — please try again or contact support.',
              },
            ],
            modalTitle: 'Processing errors',
            libraryId: media.libraryId,
            batchKind: 'media-processing-errored',
          });
          trackingStore.uploadRichEventShown({
            kind: 'processing-error',
            rowCount: 1,
          });
        }
        onClose();
      }
    }, [open, media.status]);

    const onOpen = () => {
      if (open) return;

      if (externalOpen !== undefined) {
        // If external open prop is provided, we can't control the state directly
        // This should not happen in normal usage
        return;
      }

      trackingStore.momentModalOpened({
        variant: isSearchResult ? 'search-result' : 'library-media-card',
        mediaId: media._id,
        libraryId: media.libraryId,
      });
      setInternalOpen(true);
      if (isSearchResult && result) {
        playerStore.setMoment({ start: result.startTime, end: result.endTime });
      } else {
        // For library media, show full video
        playerStore.setMoment(null);
        playerStore.setMode('full');
      }
    };

    const onClose = (event?: any, reason?: string) => {
      if (event) {
        event.stopPropagation();
      }

      trackingStore.momentModalClosed({
        mediaId: media._id,
        context: isSearchResult ? 'search' : 'library',
      });

      if (externalOpen !== undefined) {
        // If external open prop is provided, call the onClose callback
        if (props.onClose) props.onClose();
        return;
      }

      setInternalOpen(false);
      playerStore.setMoment(null);
      playerStore.setMode('moment');
      playerStore.setCurrentTime(0); // Reset current time to prevent carrying over to next video
      setIsLoading(true);
      if (props.onClose) props.onClose();
    };

    const handleDownload = async () => {
      if (!isSearchResult || !result) return;

      trackingStore.momentDownloadStarted({
        mediaId: media._id,
        format,
        removeWatermark: removeWatermark === 'yes',
      });

      if (
        !billingStore.hasEnoughCredits(billingStore.creditsLeft, watermarkRemovalCost) &&
        !isWatermarkEnabled
      ) {
        toast.custom(
          (t) => (
            <Toast
              id={t}
              header="Insufficient credits"
              description={`Downloading without watermarks would cost ${formatCreditsWithUnit(watermarkRemovalCost, 'up')} and you have ${billingStore.creditsLeft !== null ? (billingStore.creditsLeft < 0 ? 'no credits' : formatCreditsWithUnit(billingStore.creditsLeft, 'down')) : 'no credits'} left.`}
              variant="error"
            />
          ),
          { duration: 3000 },
        );
        return;
      }

      let toastId: string | number | undefined;
      try {
        toastId = toast.custom(
          (t) => (
            <Toast
              id={t}
              header={`Downloading moment as ${format === 'mp4' ? 'MP4' : format === 'gif' ? 'GIF' : 'MP4 and GIF'}`}
              description="Please wait a bit."
              variant="success"
            />
          ),
          { duration: Infinity },
        );

        const promises = [];

        if (format === 'mp4' || format === 'both') {
          promises.push(
            libraryManager.renderer.createCompilation(
              [
                {
                  libraryId: media.libraryId,
                  mediaId: media._id,
                  startTime: playerStore.moment?.start ?? result.startTime,
                  endTime: playerStore.moment?.end ?? result.endTime,
                },
              ],
              `${media.metadata?.name}_${result.startTime}-${result.endTime}.mp4`,
              720,
              isWatermarkEnabled,
            ),
          );
        }
        if (format === 'gif' || format === 'both') {
          promises.push(
            libraryManager.renderer.createGif(
              media.libraryId,
              media._id,
              playerStore.moment?.start ?? result.startTime,
              playerStore.moment?.end ?? result.endTime,
              `${media.metadata?.name}_${result.startTime}-${result.endTime}.gif`,
            ),
          );
        }

        await Promise.all(promises);

        trackingStore.momentDownloadCompleted({
          format,
          removeWatermark: !isWatermarkEnabled,
          mediaId: media._id,
          libraryId: media.libraryId,
          durationSeconds: totalSeconds,
        });

        // Refetch credits after successful download
        await billingStore.refetchCreditsAfterOperation();
      } catch (error) {
        trackingStore.momentDownloadFailed({
          mediaId: media._id,
          libraryId: media.libraryId,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        toast.custom(
          (t) => (
            <Toast id={t} header="Error" description="Failed to download moment" variant="error" />
          ),
          { duration: 3000 },
        );
      }
      if (toastId) {
        toast.dismiss(toastId);
      }
    };

    const handleMobileDownload = () => {
      setIsDownloadDrawerOpen(false);
      handleDownload();
    };

    const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };

    // Hand the marked RANGE to an agent rather than the whole media, which is the whole reason
    // this lives in the modal. The moment card offers an agent verb too (`Moment.tsx`), but a
    // card only knows its result's original bounds; the scrub bar and the filmstrip in here are
    // where the range is editable, so this is the only path that can send an edited one.
    //
    // The `??` fallbacks are belt-and-braces, not a second code path: on the search variant the
    // store is seeded with the result's bounds when the modal opens, and on the library variant
    // this handler is not passed to the bar at all until a range exists — so `moment` is set
    // whenever a click can reach here. They're kept so the handoff can't silently send
    // `undefined` timestamps if that ever moves. Same shape `handleDownload` uses.
    //
    // `control` is a PARAMETER, not a constant. Three controls reach this — the accented CTA,
    // the ⋯ row and the mobile drawer row — and reporting one hardcoded value for all of them
    // would make the redesign's own question ("does burying the verbs cost their usage?")
    // unanswerable on the surface where the CTA is loudest. It shipped that way for one commit.
    const handleAgentOnRange = (control: MediaActionControl) => {
      const range = playerStore.moment;
      const beginTimestamp = range?.start ?? result?.startTime;
      const endTimestamp = range?.end ?? result?.endTime;
      if (beginTimestamp === undefined || endTimestamp === undefined) {
        // Only reachable through a broken invariant (see above), and the symptom is a button
        // that does nothing at all on three controls at once — no chat, no toast, nothing to
        // grep for. One line turns that into a named surface and a media id.
        console.warn(
          '[MomentModal] work-with-agent fired with no marked range and no result bounds',
          {
            mediaId: media._id,
          },
        );
        return;
      }

      if (isSearchResult) {
        trackingStore.searchAskAnAgentClicked({
          mediaId: media._id,
          libraryId: media.libraryId,
          control,
        });
      } else {
        // The library modal otherwise reaches the Agent through
        // `useMediaMenuActions.executeAction`, which fires this on the way. `onWorkWithAgent`
        // routes around that dispatcher (see `MediaActionBar`), so the event is fired here
        // instead — narrowing the verb's target must not change what the surface reports.
        // This is the obligation that prop's docblock names, paid.
        trackingStore.mediaMenuAction({
          action: 'work-with-agent',
          mediaId: media._id,
          libraryId: media.libraryId,
          control,
        });
      }

      askAgentAboutMoments(
        media.libraryId,
        {
          libraryId: media.libraryId,
          // A range marked on library media answers no search, so there is no `resultType` to
          // carry — but this is not a label either. The chat app parses it out of the
          // `moments=` param and carries it through as the clip's `type`, which reaches the
          // model, so it is an assertion about the clip and has to be one the library supports.
          // A transcript-only library skips visual indexing outright, which is why the visual
          // fetch above is not even issued for one; saying `'visual'` there describes a track
          // that was never produced.
          //
          // `'visual'` stays the answer for everything else, including a library whose fetch
          // failed (`isTranscriptOnly` reads false while unknown): it is the default a search
          // carries anyway, and the chat app itself folds a type it cannot represent into it.
          // Deliberately NOT keyed on the fetched `visualDescriptions` — that is a DataFrame,
          // so its absence means "the fetch failed, or was skipped", not "there is no visual
          // data" (`.claude/rules/dataframe-absence.md`).
          searchType: isSearchResult
            ? libraryManager.multiLibSearch.resultType
            : isTranscriptOnly
              ? 'transcript'
              : 'visual',
          mediaId: media._id,
          beginTimestamp,
          endTimestamp,
        },
        // A `control` distinct from the card's `'moment-overlay'` is what keeps trimmed and
        // untrimmed handoffs separable in Mixpanel — otherwise the one capability this adds
        // is invisible in the data. Free-form `string` on `AgentsExitContext`, so no type
        // change is needed.
        { surface: isSearchResult ? 'search' : 'library', control },
      );
    };

    /**
     * Whether a range is marked at all.
     *
     * On the search variant this is always true while the modal is open (the result seeds it),
     * which is exactly why the Moment / Full toggle was already offered there unconditionally.
     * On the library variant it becomes true the moment the viewer marks something.
     */
    const hasSelection = !!playerStore.moment;

    /**
     * Undo, which means two different things by variant — see `SelectionBar`.
     *
     * Clearing has to widen the mode as well. With `moment` null, `clipRange` already falls
     * back to the whole media, so a cleared range left in `'moment'` mode plays everything
     * under a toggle that still says "Moment".
     */
    const handleUndoSelection = () => {
      if (isSearchResult && result) {
        playerStore.setMoment({ start: result.startTime, end: result.endTime });
        return;
      }
      playerStore.setMoment(null);
      playerStore.setMode('full');
    };

    /** A scene adopted as the range, from the Scenes panel. */
    const handleSelectRange = (start: number, end: number) => {
      playerStore.setMoment({ start, end });
      // Marking is a `full`-mode job; see the note on `selection` in `VideoPlayer`. Widening
      // here also means a scene picked while previewing a previous range doesn't leave the
      // viewer looking at a bar that just silently rescaled under them.
      playerStore.setMode('full');
    };

    /**
     * A comment's "Copy link": its media, on the Comments tab, at the comment's moment, with the
     * comment picked out — see `getCommentUrl`. The media's library name goes into the slug even
     * from a search result, whose own modal has no address: the link opens the library page.
     */
    const handleCopyCommentLink = async (comment: MediaComment) => {
      const path = getCommentUrl(
        media.libraryId,
        media._id,
        { id: comment.id, startSeconds: anchorStart(comment.anchor) },
        { libraryName: libraryForMedia?.name, mediaName: media.metadata?.name },
      );
      if (await copyAppLink(path)) {
        showToast('comment-link-copied', { label: formatAnchorLabel(comment.anchor) });
      } else {
        showToast('media-link-copy-failed');
      }
    };

    /**
     * Which comment link has been honoured, as `<mediaId>:<commentId>` — one-shot, like
     * `appliedStartAtRef`. Without it, every later re-render would put the selection back on the
     * linked comment after the reader had moved on, and a span-anchored one would re-mark its
     * range over whatever they had marked since.
     */
    const appliedCommentLinkRef = useRef<string | null>(null);

    // Picks out the comment a link points at, once this media's comments have been ANSWERED —
    // not merely present. `fetching` is checked alongside `data` because the store is per media
    // and outlives a close: reopening a media holds the previous visit's list while the fresh read
    // is in flight, and a link to a comment written since would be judged "not found" against it.
    //
    // Checked live inside the effect, not from the render-time value in the deps. The render that
    // opens the modal happens before the effect above starts the fresh read, so at that render the
    // store still looks answered — by the previous visit. By the time this effect runs, that read
    // has begun and `fetching` is true. The deps value is only the signal to look again.
    //
    // A failed read decides nothing (no toast): the list says it could not load, and "that comment
    // was deleted" would be a claim the app cannot back.
    const linkedCommentsAnswered = !commentsStore.fetching && commentsStore.data !== null;
    useEffect(() => {
      if (!open || !linkedCommentId) return;
      if (commentsStore.fetching || !commentsStore.data) return;
      const key = `${media._id}:${linkedCommentId}`;
      if (appliedCommentLinkRef.current === key) return;
      appliedCommentLinkRef.current = key;

      const comment = commentsStore.data?.find((candidate) => candidate.id === linkedCommentId);
      if (!comment) {
        showToast('comment-link-not-found');
        return;
      }
      // `?t=` already brought the player here.
      goToComment(comment, { seek: false });
      // `goToComment` only touches state setters and the stable player store, so it is safe to
      // leave out.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, linkedCommentId, linkedCommentsAnswered, media._id, commentsStore]);

    /**
     * Go to a comment, from wherever it was picked: its thread becomes the selected one, a
     * span-anchored comment hands its span over as the marked range, and the player goes to it
     * unless it is already there. The one definition behind the panel's timestamp chip, a marker
     * press and a comment link, so all three leave the same mark.
     */
    const goToComment = (comment: MediaComment, { seek }: { seek: boolean }) => {
      setSelectedCommentId(comment.id);
      if (seek) seekToMediaTime(anchorStart(comment.anchor));
      if (comment.anchor.type === 'range') {
        handleSelectRange(comment.anchor.startTime, comment.anchor.endTime);
      }
    };

    /**
     * A comment marker was pressed, on the scrub bar or on the filmstrip.
     *
     * Opens the panel as well as seeking, because the marker says only that a note exists —
     * reading it is the point of pressing one. That goes through `handlePanelChange`, the same
     * path the tab row uses, so pressing a marker is remembered as landing on Comments exactly
     * as pressing the tab is; a marker press is a request for this panel like any other.
     *
     * The panel's own chip calls `goToComment` directly: it is already on Comments.
     */
    const handleSelectComment = (commentId: string) => {
      handlePanelChange('comments');
      const comment = commentsStore.data?.find((candidate) => candidate.id === commentId);
      if (comment) goToComment(comment, { seek: true });
    };

    /**
     * Start a comment: the Comments panel, with its composer focused. The selection bar's Comment
     * button and the `C` key both land here, and do the same thing.
     *
     * Through `handlePanelChange` like a marker press, and for the same reason — it is a request
     * for that panel. No anchor is passed along: the composer pins the marked range if there is
     * one and the frame under the playhead if not (`pinHere`), which is exactly what both entry
     * points mean.
     */
    const handleStartComment = () => {
      handlePanelChange('comments');
      setComposerFocusRequested(true);
    };
    const handleStartCommentRef = useRef(handleStartComment);
    handleStartCommentRef.current = handleStartComment;

    // `C` starts a comment, as it does in review tools. Handled here rather than through
    // `useKeyboardShortcut`, which is for modifier chords: a bare letter is also a character, so
    // it needs the typing guard, and it belongs to this modal alone — the same arrangement as the
    // player's space and arrow keys (`useKeyboardEvents`).
    //
    // Off wherever the composer cannot take the focus it asks for: a read-only library has no
    // composer, and a processing media shows a placeholder in its place. An unanswered request
    // would sit pending until the modal closed.
    useEffect(() => {
      if (!open || !canComment || isProcessing) return;
      const onKeyDown = (event: KeyboardEvent) => {
        if (!matchesKey(event, KEY_SHORTCUTS.startComment)) return;
        if (isTextEntryTarget(event.target)) return;
        // Someone nearer the key already claimed it — an open menu's type-to-select, say. This
        // listens on `document`, so it runs after them and must not fire as well.
        if (event.defaultPrevented) return;
        // Claimed, so nothing else acts on the same press — a browser's find-as-you-type, for one.
        event.preventDefault();
        handleStartCommentRef.current();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, canComment, isProcessing]);

    /**
     * Whether there is anything worth stating under the player.
     *
     * Not the same question as `hasSelection`. A moment opened from search arrives with a range
     * already — the result's own bounds — and announcing "Selected 0:42 – 1:05" for something
     * the user did not select is chrome that is always on and never says anything new, next to
     * a Reset that would do nothing. It earns its place once the range stops being the one the
     * result came with. On library media any range at all is one the user marked.
     *
     * The epsilon is because the range comes back off a pointer position: dragging a handle and
     * putting it back lands on a float a hair from where it started, which is not an edit.
     */
    const isSelectionEdited =
      hasSelection &&
      (!result ||
        Math.abs(playerStore.moment!.start - result.startTime) > SELECTION_EDIT_EPSILON_SECONDS ||
        Math.abs(playerStore.moment!.end - result.endTime) > SELECTION_EDIT_EPSILON_SECONDS);

    /**
     * Retires the hint the first time this user marks a range, anywhere.
     *
     * An effect because the two ways to mark one do not meet: the scene button comes through
     * this component, but a drag on the scrub bar is written straight to the store by
     * `VideoPlayer`. Watching the fact itself catches both without threading a callback down
     * through the player for a single boolean.
     *
     * Library variant only. A search result arrives with a range it did not ask for, so
     * counting that as "has marked one" would retire the hint for someone who has never done
     * it.
     */
    useEffect(() => {
      if (!isSearchResult && hasSelection && !uiPreferences.hasMarkedMediaRange) {
        uiPreferences.markMediaRangeUsed();
      }
    }, [isSearchResult, hasSelection, uiPreferences]);

    /**
     * One row under the player, in one of three states: the range, the hint that teaches how to
     * make one, or nothing.
     *
     * The hint and the readout share the slot deliberately — marking a range swaps the copy
     * instead of pushing the panel down, so the line that taught the gesture is the same line
     * that confirms it worked.
     */
    const selectionBar = isSelectionEdited ? (
      <SelectionBar
        start={playerStore.moment!.start}
        end={playerStore.moment!.end}
        variant={isSearchResult ? 'reset' : 'clear'}
        onUndo={handleUndoSelection}
        onComment={canComment ? handleStartComment : undefined}
      />
    ) : !isSearchResult && !hasSelection && !uiPreferences.hasMarkedMediaRange ? (
      <SelectionHint />
    ) : null;

    /**
     * Whether the action bar may show its accented CTA at all.
     *
     * `completed` for the same reason the media menu gates its verbs (`useMediaVerbs`): a
     * still-processing media has no indexed content for an agent to work with, and this modal
     * shows a placeholder instead of a timeline anyway. Unlike the verbs — which stay in the
     * kebab, dimmed, with their reasons — the CTA is hidden outright, because an accented pill
     * saying "wait" is a loud promise on a surface whose right-hand panel is already a
     * processing placeholder.
     *
     * The debug toggle is folded in so "Simulate processing modal" exercises the real path
     * rather than a modal that claims to be processing while still offering the CTA.
     *
     * `libraryForMedia?.data` is the third condition, and it is NOT a redundant null-check.
     * Which verb leads is decided by `isTranscriptOnly`, and `LibraryStore.isTranscriptOnly` is
     * `!!this.data && …` — so a library whose fetch failed, or simply hasn't landed, reads as
     * "not transcript-only" rather than "unknown". Without this gate a podcast library shows
     * "Chat with an Agent" and then swaps the primary button's label and glyph under the user's
     * cursor when the store recovers. `.claude/rules/dataframe-absence.md` calls this exact
     * shape out and prescribes the trade taken here: fail toward silence — a button that
     * arrives a beat late beats one that changes identity after you've read it.
     */
    const isCtaReady =
      media.status === 'completed' &&
      !debugStore.simulateProcessingModal &&
      !!libraryForMedia?.data;

    /**
     * When there is a marked range, the agent verb hands THAT over instead of the whole media.
     *
     * Only the handler changes — the verb keeps the label, glyph and inert-and-why that
     * `useMediaVerbs` gave it, because it is one capability over a narrower target. Passing
     * `undefined` leaves the media-scoped behaviour in place, which is what an unmarked library
     * media gets: nothing about the button changes until the user has said what to scope it to.
     *
     * Not on Atlas. The search path reads `multiLibSearch.resultType` for its segment type,
     * which is meaningless there — so Atlas keeps the media-scoped verb and reports its own
     * `AgentsSurface`, rather than folding those exits into the library's numbers.
     */
    const onWorkWithAgent = surface === 'atlas' || !hasSelection ? undefined : handleAgentOnRange;

    /**
     * The range, said on the control that consumes it and again at length in its tooltip.
     *
     * The sentence follows the `outcome` strings in `@lib/verbs` ("Opens a new chat about this
     * library"), so the two read as the same voice — this is that sentence with a narrower
     * subject. `MediaActionBar` decides where it lands: the accented pill when the agent verb
     * is the pill, the ⋯ / drawer row when it is not.
     */
    const scopeRange = onWorkWithAgent
      ? `${formatClockLabel(playerStore.moment!.start)} – ${formatClockLabel(
          playerStore.moment!.end,
        )}`
      : undefined;
    const agentScope = scopeRange
      ? {
          label: scopeRange,
          description: `Opens a new chat about ${scopeRange} of this media`,
        }
      : undefined;

    /**
     * The bar, built once and rendered by both layouts.
     *
     * The variant split is NOT a product choice — `LibraryMediaActionBar` reaches
     * `useMediaMenuActions`, which calls `useLibraryFolder()` and `useMediaDeleteModal()`, and
     * neither provider is mounted on `/search` or `/atlas`. Rendering it there throws. That's
     * why the two are separate components rather than one with a flag: a condition may guard a
     * *component*, never a hook.
     *
     * The search side therefore offers everything except rename, retry and delete — absent
     * rather than dimmed, because off the library page there is no single library in context to
     * perform them against.
     */
    /**
     * "Download .srt", which used to be a labelled pill sitting in the panel's tab row.
     *
     * It moved into the ⋯ for two reasons. It was the same shape, size and near enough the same
     * colour as the tab beside it — a white pill next to "Transcript", which is white when
     * selected — so a download button read as a fourth tab. And it was taking ~126px of a row
     * that had to fit four tabs, which is what pushed Comments off the end of it.
     *
     * It cannot come from `useMediaVerbs` with the rest of the menu: it needs the fetched
     * transcript, which this modal holds and a grid card has never asked for. That is what
     * `MediaMenuHostItem` is for.
     *
     * Offered whenever there IS a transcript, not only while the Transcript tab is open. The
     * file is a fact about the media, not about which panel is showing, and a menu whose rows
     * appear and disappear as you click tabs is a menu you cannot learn.
     *
     * That widens where it can be reached — the pill was desktop-only and only on the
     * Transcript tab — but it does cost the desktop user who was already using it one click.
     * `trackingStore.transcriptDownloaded` below is the only call site of that event, so the
     * trade is measurable: ijw-fyi/web.front.v2#856 books reading it against the pre-#854
     * baseline, and names surfacing the row back beside the tabs as the revert path.
     */
    const transcriptDownloadItem: MediaMenuHostItem[] | undefined =
      transcript && transcript.segments.length > 0
        ? [
            {
              id: 'download-srt',
              // Kept short because it has to be: the app's theme pins every `MuiMenuItem` to a
              // literal `width: 199px` (`theme.ts`), so a row does not wrap or ellipsise when it
              // outgrows that — it just clips mid-word. "Download transcript (.srt)" needed
              // ~231px and did exactly that. The quality menu carries a workaround for the same
              // box; this is the second thing that box has broken.
              label: 'Download transcript',
              icon: <DownloadIcon fontSize="small" />,
              onSelect: () => {
                trackingStore.transcriptDownloaded(media._id);
                downloadSrt(
                  transcript,
                  media.metadata?.name ? `${media.metadata?.name}.srt` : 'transcript.srt',
                );
              },
            },
          ]
        : undefined;

    const actionBar = (layout: MediaActionBarLayoutProps = {}) =>
      !isSearchResult ? (
        <LibraryMediaActionBar
          media={media}
          isModifiable={isModifiable}
          ctaControl="media-modal-cta"
          secondaryControl="media-modal-secondary"
          isTranscriptOnly={isTranscriptOnly}
          canShowCta={isCtaReady}
          getCurrentTime={getCurrentPlayheadTime}
          onWorkWithAgent={onWorkWithAgent}
          agentScope={agentScope}
          hostItems={transcriptDownloadItem}
          {...layout}
        />
      ) : (
        <MediaVerbsActionBar
          media={media}
          ctaControl="media-modal-cta"
          secondaryControl="media-modal-secondary"
          surface={surface === 'atlas' ? 'atlas' : 'search'}
          agentScope={agentScope}
          onWorkWithAgent={onWorkWithAgent}
          // `find-moments` goes to `/search?libs=…&media=…`. On /search that's a query change,
          // not a route change: nothing unmounts, so without this the modal would stay open
          // over a grid that had silently re-scoped to a single media, with no feedback that
          // anything happened. (The library and Atlas variants navigate off-route and unmount.)
          onWillNavigate={onClose}
          isTranscriptOnly={isTranscriptOnly}
          canShowCta={isCtaReady}
          getCurrentTime={getCurrentPlayheadTime}
          hostItems={transcriptDownloadItem}
          {...layout}
        />
      );

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    /**
     * The name on the left, the controls in one right-aligned cluster — never controls that
     * follow the name. An older version let them trail the title, which landed them wherever its
     * length left them, ~700px of dead header from the ✕. See `$panel-column` in the stylesheet
     * for why they no longer sit over the tab row either.
     */
    // Where "Show in library" goes — `?mediaId=` opens the folder that holds the media — named on
    // the button when that is a folder rather than the library's top level.
    const mediaFolder = libraryForMedia?.folderOf(media) ?? FOLDER_ROOT_PATH;
    const mediaFolderEntry = libraryForMedia?.foldersByPath.get(mediaFolder);
    const locationTrail = folderTrail(mediaFolder, libraryForMedia?.name ?? 'library');

    const customHeader = (
      <>
        <div className={css.headerMain}>
          {/* Native `title`, matching the library header's `<h2 title={library.name}>`: the name
              is ellipsised now, so there has to be some way to read the whole thing. */}
          <span className={css.title} title={media.metadata?.name}>
            {media.metadata?.name}
          </span>
          {/* Only show for search results variant, not library page. Beside the name rather than
              with the actions: it is about this media, not about the panel. */}
          {isSearchResult && (
            <Tooltip title={`Show in ${locationTrail}`} arrow placement="top">
              <IconButton
                className={classNames(mediaFolderEntry && css.libraryLinkWithFolder)}
                onClick={() => {
                  trackingStore.momentModalShowInLibrary({
                    mediaId: media._id,
                    libraryId: media.libraryId,
                  });
                  navigateTo(
                    getLibraryUrl(media.libraryId, libraryForMedia?.name, {
                      highlightMediaId: media._id,
                    }),
                  );
                }}
              >
                <span className={css.libraryIcon}>
                  <LibraryIcon size={20} />
                </span>
                {mediaFolderEntry && (
                  <span className={css.libraryFolder}>
                    <FolderGlyph color={mediaFolderEntry.color} size={16} />
                    <span className={css.libraryFolderName}>{mediaFolderEntry.name}</span>
                  </span>
                )}
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div className={css.headerRight}>
          {/* One accented CTA (with clipping's quieter pill before it when the Agent leads) plus
              one ⋯, on BOTH variants — this used to be a three-glyph rail
              beside a kebab on the library variant, and a lone clip-verb rail on the search one.
              "All buttons should be under the 3 dots, except a big expanded one on the right
              which should be based on the library type" (Media Options, #Bugs & Features).

              The CTA's identity follows the library: clipping in a transcript-only library, the
              Agent otherwise — decided by `splitMediaActions`, not here. */}
          {actionBar()}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </>
    );

    const footerContent = isSearchResult ? (
      <>
        <div className={css.optionsGrid}>
          <div className={css.gridHeader}>
            <span className={css.formatLabel}>Select format</span>
            <div className={css.watermarkLabelContainer}>
              <span className={css.watermarkLabel}>Remove StarZero watermark</span>
              <Tooltip title={watermarkRemovalTooltipText} arrow placement="top">
                <InfoRoundedIcon className={css.infoIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={css.gridControls}>
            <MultipleToggle
              className={css.compactToggle}
              options={[
                { value: 'mp4', label: 'MP4' },
                { value: 'gif', label: 'GIF' },
                { value: 'both', label: 'Both' },
              ]}
              value={format}
              onChange={(val) => {
                setFormat(val);
                trackingStore.momentFormatChanged({ format: val });
              }}
            />
            <MultipleToggle
              className={css.compactToggle}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              value={removeWatermark}
              onChange={(val) => {
                setRemoveWatermark(val);
                trackingStore.momentWatermarkToggled({ removeWatermark: val });
              }}
            />
          </div>
        </div>
        <button
          className={css.cancelButton}
          onClick={() => {
            trackingStore.momentModalCancelled({ mediaId: media._id });
            onClose();
          }}
        >
          Cancel
        </button>
        <button
          className={css.downloadButton}
          onClick={handleDownload}
          disabled={libraryManager.renderer.isRendering}
        >
          Download
        </button>
      </>
    ) : null;

    /**
     * The right-hand panel, built once and rendered by both layouts: three panels across two
     * layouts would otherwise be six places to keep in step.
     *
     * A total map rather than a ternary chain, so widening `ModalTab` is a compile error here
     * instead of a fourth tab silently rendering the transcript. Only the selected entry is
     * mounted; the rest are plain element objects.
     */
    const panelsByTab: Record<ModalTab, ReactNode> = {
      visual: (
        <VisualDescriptionLoader
          visualDescriptions={visualDescriptions}
          error={visualDescriptionsError}
          store={playerStore}
        />
      ),
      scenes: (
        <ScenesPanel
          scenes={scenesFile}
          threads={threadsFile}
          error={scenesError}
          durationSeconds={media.metadata?.video?.duration ?? 0}
          onSeek={seekToMediaTime}
          onSelectRange={handleSelectRange}
          store={playerStore}
        />
      ),
      transcript: (
        <TranscriptLoader
          transcript={transcript}
          error={transcriptError}
          scenes={scenesFile?.scenes}
          videoPlayerRef={videoPlayerRef}
          store={playerStore}
        />
      ),
      comments: (
        <CommentsPanel
          store={commentsStore}
          playerStore={playerStore}
          canWrite={canComment}
          selectedCommentId={selectedCommentId}
          onGoToComment={(comment) => goToComment(comment, { seek: true })}
          focusComposer={composerFocusRequested}
          onComposerFocused={handleComposerFocused}
          sort={commentSort}
          onSortChange={setCommentSort}
          onCopyLink={handleCopyCommentLink}
        />
      ),
    };
    const panelContent = panelsByTab[currentPanel];

    if (isMobile) {
      return (
        <MobileModal
          open={open}
          onClose={onClose}
          title={
            <span className={css.mobileTitle} title={media.metadata?.name}>
              {media.metadata?.name}
            </span>
          }
          noBorderBottom
          contentClassName={css.mobileBody}
          rawContent
          /* The pills are pinned here rather than put in the panel header row beside the ⋯.
             That row is already measured to overflow at 320px (see `.mobilePanelHeader` in the
             stylesheet — 314px of content in 288px of space, which is why it wraps), so a
             labelled pill in it would push onto its own line and then scroll away with the
             panel. A sticky full-width footer is where a phone's primary action belongs, and
             the slot was going spare. Renders nothing when there is no CTA — a processing
             media leaves no empty bar behind. */
          footer={actionBar({ parts: 'pills', fullWidth: true })}
        >
          {/* Mode toggle (Moment / Full media). Offered wherever there is a range to preview,
              which on the library variant means once the viewer has marked one. */}
          {hasSelection && (
            <div className={css.mobileModelToggle}>
              <MultipleToggle
                className={css.compactToggle}
                options={[
                  { value: 'moment', label: 'Moment' },
                  { value: 'full', label: 'Full media' },
                ]}
                value={playerStore.mode}
                onChange={(mode: 'moment' | 'full') => {
                  const oldMode = playerStore.mode;
                  playerStore.setMode(mode);
                  trackingStore.videoModeChanged(mode);

                  // If switching from full to moment and playhead is outside bounds, seek to
                  // start. Read off the store rather than `result`: the range is what the clip
                  // is about to become, and on a marked library media there is no result at all.
                  const range = playerStore.moment;
                  if (oldMode === 'full' && mode === 'moment' && range) {
                    const currentTime = playerStore.currentTime;
                    if (currentTime < range.start || currentTime > range.end) {
                      scrubAfterClipRebuild(range.start);
                    }
                  }
                }}
              />
            </div>
          )}

          {/* Video player (no overlay toggle on mobile — it's separate above) */}
          <div className={css.mobileVideoSection}>
            {media.status === 'queued' || isProcessingWithoutVideo(media) ? (
              <VideoProcessingPlaceholder />
            ) : (
              <>
                {isLoading && <VideoSkeleton className={css.skeletonVideo} />}
                <VideoPlayer
                  media={media}
                  ref={videoPlayerRef}
                  hidden={isLoading}
                  onReady={() => setIsLoading(false)}
                  withModeToggle={false}
                  store={playerStore}
                  showScanningEffect={isProcessingHovered && isProcessing}
                  scenes={scenesFile?.scenes}
                  comments={commentsStore.data ?? undefined}
                  selectedCommentId={selectedCommentId}
                  onSelectComment={handleSelectComment}
                  selectableRange
                />
                {/* Inside this branch, not after it: a media still being processed shows a
                    placeholder instead of a player, and a line telling the viewer to drag a bar
                    that is not on screen is worse than no line.

                    No `SelectionSlot` here, deliberately: this player is width-bound, so the
                    row appearing below it costs the scrub bar no width — see `SelectionSlot`. */}
                {selectionBar}
              </>
            )}
          </div>

          {/* Scrollable content area */}
          <div className={css.mobileContent}>
            {isProcessing || isProcessingWithoutVideo(media) ? (
              <ProcessingPlaceholder
                onHoverChange={setIsProcessingHovered}
                hasVideo={media.status !== 'queued' && !isProcessingWithoutVideo(media)}
              />
            ) : isLoading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={120}
                sx={{ borderRadius: '8px' }}
              />
            ) : (
              <>
                {/* Panel picker, with the menu and the clip/download controls pinned beside it */}
                <div className={css.mobilePanelHeader}>
                  <PanelTabs
                    value={currentPanel}
                    onChange={handlePanelChange}
                    isTranscriptOnly={isTranscriptOnly}
                    commentCount={commentsStore.count}
                    className={css.mobileTabs}
                  />
                  <div className={css.mobilePanelActions}>
                    {/* Kebab only — the footer owns the CTA on mobile. Two instances of the bar
                        in one modal, which is two instances of the action hook; that duplicate
                        predates this change (the header used to mount the rail and the kebab
                        separately) and neither touches the other's state. */}
                    {actionBar({ parts: 'kebab' })}
                    {isSearchResult && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => uiPreferences.toggleMobileTimelineVisible()}
                          sx={{
                            color: uiPreferences.mobileTimelineVisible ? 'primary.main' : 'inherit',
                          }}
                        >
                          <ContentCutIcon fontSize="small" sx={{ transform: 'rotate(-90deg)' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setIsDownloadDrawerOpen(true)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </div>
                </div>

                {/* Panel content */}
                {open && panelContent}
              </>
            )}
          </div>

          {/* Timeline zoom + timeline */}
          {isSearchResult && uiPreferences.mobileTimelineVisible && (
            <div className={css.mobileTimeline}>
              <VideoTimeline
                duration={media.metadata?.video?.duration ?? 0}
                videoPlayerRef={videoPlayerRef}
                store={playerStore}
                mediaId={media._id}
                libraryId={media.libraryId}
                isLoading={isLoading}
                comments={commentsStore.data ?? undefined}
                selectedCommentId={selectedCommentId}
                onSelectComment={handleSelectComment}
                compactZoom
                zoomLevel={timelineZoomLevel}
                onZoomLevelChange={setTimelineZoomLevel}
              />
            </div>
          )}

          {/* Mobile download drawer */}
          {isSearchResult && (
            <MobileDownloadDrawer
              open={isDownloadDrawerOpen}
              onClose={() => setIsDownloadDrawerOpen(false)}
              onOpen={() => setIsDownloadDrawerOpen(true)}
              format={format}
              onFormatChange={setFormat}
              removeWatermark={removeWatermark}
              onRemoveWatermarkChange={setRemoveWatermark}
              watermarkCostLabel={(() => {
                const credits = formatCredits(watermarkRemovalCost, 'up');
                return `Download without watermark (costs ${credits} ${Number(credits) === 1 ? 'credit' : 'credits'})`;
              })()}
              isDownloading={libraryManager.renderer.isRendering}
              onDownload={handleMobileDownload}
            />
          )}
        </MobileModal>
      );
    }

    return (
      <Modal open={open} onClose={onClose} keepMounted={false}>
        <div className={css.momentModal} onClick={handleContentClick}>
          <div className={css.header}>{customHeader}</div>
          <div className={css.preview}>
            <div className={css.videoSection}>
              {media.status === 'queued' || isProcessingWithoutVideo(media) ? (
                <VideoProcessingPlaceholder />
              ) : (
                <>
                  {isLoading && <VideoSkeleton className={css.skeletonVideo} />}
                  <VideoPlayer
                    media={media}
                    ref={videoPlayerRef}
                    hidden={isLoading}
                    onReady={() => setIsLoading(false)}
                    withModeToggle={hasSelection}
                    toggleClassName={css.compactToggle}
                    store={playerStore}
                    showScanningEffect={isProcessingHovered && isProcessing}
                    scenes={scenesFile?.scenes}
                    comments={commentsStore.data ?? undefined}
                    selectedCommentId={selectedCommentId}
                    onSelectComment={handleSelectComment}
                    selectableRange
                  />
                  {/* Inside this branch — see the note on the mobile layout's copy. The slot
                      is reserved rather than grown into, because this player is height-bound
                      and loses width to anything below it — see `SelectionSlot`. */}
                  <SelectionSlot>{selectionBar}</SelectionSlot>
                </>
              )}
            </div>
            <div className={css.rightPanel}>
              {isProcessing || isProcessingWithoutVideo(media) ? (
                <ProcessingPlaceholder
                  onHoverChange={setIsProcessingHovered}
                  hasVideo={media.status !== 'queued' && !isProcessingWithoutVideo(media)}
                />
              ) : isLoading ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{ borderRadius: '16px' }}
                />
              ) : (
                <>
                  {/* Tabs alone. "Download .srt" moved into the ⋯ — see
                      `transcriptDownloadItem` for why. */}
                  <PanelTabs
                    value={currentPanel}
                    onChange={handlePanelChange}
                    isTranscriptOnly={isTranscriptOnly}
                    commentCount={commentsStore.count}
                    className={css.panelTabs}
                  />
                  {open && panelContent}
                </>
              )}
            </div>
          </div>
          {isSearchResult && (
            <VideoTimeline
              duration={media.metadata?.video?.duration ?? 0}
              videoPlayerRef={videoPlayerRef}
              store={playerStore}
              mediaId={media._id}
              libraryId={media.libraryId}
              isLoading={isLoading}
              comments={commentsStore.data ?? undefined}
              selectedCommentId={selectedCommentId}
              onSelectComment={handleSelectComment}
            />
          )}
          {footerContent && <div className={css.footer}>{footerContent}</div>}
        </div>
      </Modal>
    );
  }),
);
